/* eslint-disable react/no-unstable-nested-components */
import React, {
    useContext, useMemo, useState, useRef, useEffect
  } from 'react';
  import { StatusEnum, TableWrapperProps } from 'models/general';
  import { useLocation, useNavigate } from 'react-router-dom';
  import IconList, { IconListModifier } from 'components/iconList';
  import { useTranslation } from 'react-i18next';
  import { StoreContext } from 'models/import';
  import Tooltip, { TooltipAlignEnum, TooltipCursorEnum, TooltipModifier } from 'components/tooltip';
  import StatusIcon, { StatusIconModifier } from 'components/statusIcon';
  import { MaterialReactTable, useMaterialReactTable,
    MRT_Virtualizer,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_SortingState,
  } from 'material-react-table';
  import {
    QueryClient,
    QueryClientProvider,
    useQuery,
  } from '@tanstack/react-query';
  import { postLaminasApiData } from 'services/api';
  import { CircularProgress, IconButton } from '@mui/material';
  import { OverviewLoadingStatus, CustomNameMap } from 'models/overview';
  import BalanceStatusTooltip, { BalanceStatusEnum, getStatus } from 'pages/overview/balanceStatusTooltip';
  import { getFormattedNumber } from 'components/table/balanceTable';
  import { ImportTableBalanceStatus } from 'components/table/importTable';
  import { TableDataRes } from 'components/table/base/tableBaseApi';
  import { BalanceCheckTableProps } from 'pages/balancecheck/balanceCheck';
  import PlatformCard, { PlatformCardModifier } from 'components/platformCard';
  import { getStripedRowsForTable, notEmpty } from 'services/util';
  import { getDateForUI } from 'services/transactions';
  import RefreshIcon from 'assets/icons/refresh.svg';

  export interface BalanceCheckLeftTableDef {
    customName: string;
    lastUpdated: number | null;
    balanceStatusSummary: ImportTableBalanceStatus[] | null;
    totalValue: number | null;
    transactionsCount: number | null;
    coinTickerSummary: string[] | null;
    containsLiveData: boolean | null;
  }

  function BalanceCheckLeftTable(props: BalanceCheckTableProps) {
    const tableRef = useRef(); // <<MRT_TableInstance<TData> | null>>;

    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { userSettings, themeName } = useContext(StoreContext);

    const [loadingStatus, setLoadingStatus] = useState(OverviewLoadingStatus.FIRST_LOAD);
    const cachedDataKeyValue = props.routeName+'_table';

    const [cacheDateTime, setCacheDateTime] = useState(null);
    const [refreshCacheAction, setRefreshCacheAction] = useState<number | null>(null);

    let controller = new AbortController();
    let controllerDetailLoad = new AbortController();

    const showLoadingBars = (): boolean => loadingStatus !== OverviewLoadingStatus.SECOND_LOAD;

    const onNavigate = (url: string | null) => {
      if (url && url.length) {
        window.location.href = url;
      }
    };

    const applyFilters = (data: BalanceCheckLeftTableDef[]): BalanceCheckLeftTableDef[] => {
        var items = data;
        if (props.showReportedBalances || props.warningsUpdate) {
            items = items.filter((item) => {
                if (!item.balanceStatusSummary) return true;

                const liveDataCheck = item.containsLiveData || false;
                const status = getStatus(item.balanceStatusSummary, false, liveDataCheck);
                const showWarningCheck = status === StatusEnum.WARNING || status === StatusEnum.GRAY_OUTLINE;

                if (props.showReportedBalances && props.warningsUpdate) {
                    return liveDataCheck && showWarningCheck;
                } else if (props.showReportedBalances) {
                    return liveDataCheck;
                } else if (props.warningsUpdate) {
                    return showWarningCheck;
                } else {
                    return false;
                }
            });
        }
        return items;
    };

    const onDetailNavigate = (
      customName: string | null,
    ) => {
      if (!customName) { return; }// on first load it will call click with null

      controller.abort();// cancel ongoing when navigating to detail
      controllerDetailLoad.abort();
      setLoadingStatus(OverviewLoadingStatus.FIRST_LOAD);// reset status on pagination
      controller = new AbortController();// reset controller
      controllerDetailLoad = new AbortController();

      const { origin } = window.location;
      window.history.replaceState(null, '', `${origin}/${props.routeName}`);

      try {
        window.localStorage.setItem(cachedDataKeyValue, JSON.stringify({
          'tableData' : tableData,
          'loadedDetailedInfo' : loadedDetailedInfo,
          'loadedRequestUri' : loadedRequestUri,
        }));
      } catch {}
      props.goToDetailSideEffect();

      navigate(
        `/${props.routeName}/${encodeURIComponent(customName)}`, {
            state: { data: props.currentMismatch, dataCurrency: props.userCurrencyMismatch },
          }
        );
    };

    const getCustomNameMapping = (i: BalanceCheckLeftTableDef): CustomNameMap => {
      if (i.customName === t('* no value *', { ns: 'overview' })) {
        return {
          customNameForApi: '',//api only accepts empty as parameter to map this
          customNameForMapping: '* no value *',//frontend accepts only value we get from api
        }
      } else {
        return {
          customNameForApi: i.customName,
          customNameForMapping: i.customName,
        }
      }
    }

  // Details is already loaded in first call sometimes, mark those results as already loaded
  const getAlreadyLoadedDetails = (data: BalanceCheckLeftTableDef[]): string[] => {
    return data.map((i) => {
      if (i.balanceStatusSummary
        && i.totalValue !== null
        && i.coinTickerSummary !== null
        && i.containsLiveData !== null
      ) {
        return i.customName;
      }
      return null;
    }).filter(notEmpty);
  };

  const hasLiveData = (data: BalanceCheckLeftTableDef[]): boolean => {
    return data.some((i) => i.containsLiveData);
  };

    const {
      data, isError, isFetching, isLoading, refetch,
    } = useQuery<TableDataRes<BalanceCheckLeftTableDef, any>>({
      queryKey: [{
        filter: props.filters, // additional external filters
        userId: userSettings?.userId,
        refreshCacheAction: refreshCacheAction,
        controller,
        tableData: props.data,
      },
      ],
      queryFn: async (item) => {
        if (props.filters === '' || props.filters === '{}') {
          props.setShowSpinners(false);
          return null;
        }

        controllerDetailLoad.abort();// cancel ongoing when navigating to detail
        controllerDetailLoad = new AbortController();// reset controller

        //cached data on returning from detail
        if (!props.data) {
          const val = window.localStorage.getItem(cachedDataKeyValue);
          if (val) {
            window.localStorage.removeItem(cachedDataKeyValue);
            setLoadingStatus(OverviewLoadingStatus.NORMAL_LOAD);
            props.setShowSpinners(false);
            const value = JSON.parse(val);
            if (value.tableData && value.loadedRequestUri && value.loadedDetailedInfo) {
              setTableData(value.tableData);
              setLoadedRequestUri(value.loadedRequestUri);
              setLoadedDetailedInfo(value.loadedDetailedInfo);
              return value;
            }
          }
        }

        if (!refreshCacheAction && data) {
          return data;
        }

        props.setShowSpinners(true);

        //filter change always first load
        setLoadingStatus(OverviewLoadingStatus.FIRST_LOAD);
        let localLoadingStatus = OverviewLoadingStatus.FIRST_LOAD;

        const filterParameters = props.filters ? props.filters : '';
        const preloadParameter = (localLoadingStatus === OverviewLoadingStatus.FIRST_LOAD && !refreshCacheAction) ? 'preload=1' : 'preload=0';
        const needsReloadParam = (localLoadingStatus === OverviewLoadingStatus.FIRST_LOAD && refreshCacheAction) ? 'filter[forceRefreshLiveDataCache]=1' : 'filter[forceRefreshLiveDataCache]=0';
        try {
          const response = await postLaminasApiData<TableDataRes<BalanceCheckLeftTableDef, any>>(
            '/tx-overview-v2/platform-overview',
            `${filterParameters}&${preloadParameter}&${needsReloadParam}`,
            controllerDetailLoad.signal,
            {'Content-Type': 'application/x-www-form-urlencoded'}
          );
          if (localLoadingStatus === OverviewLoadingStatus.FIRST_LOAD) {
            setLoadingStatus(OverviewLoadingStatus.SECOND_LOAD);
          } else {
            setLoadingStatus(OverviewLoadingStatus.NORMAL_LOAD);
          }
          if (refreshCacheAction) {
            setRefreshCacheAction(null);
          }
          props.setShowSpinners(false);
          props.onLeftTableLoaded(response);

          if (response && response.liveDataCacheTimestamp) {
            setCacheDateTime(response.liveDataCacheTimestamp);
          } else {
            setCacheDateTime(null);
          }

          setTableData(response.data);
          setLoadedRequestUri(props.filters);
          setLoadedDetailedInfo(getAlreadyLoadedDetails(response.data));
          return response;
        } catch (error) {
          setLoadingStatus(OverviewLoadingStatus.LOADING_ERROR);
          props.setShowSpinners(false);
          if (localLoadingStatus !== OverviewLoadingStatus.FIRST_LOAD || error.message === 'canceled') {
            // return cache if we cancel or on subsequent loads
            return data;
          }
          setIsError(true);// we need to maually set the framework table error on network error since we're catching the error here
          setTableData([]);
          setLoadedDetailedInfo([]);
          return null;// network error
        }
      },
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    });

    //detail load

    const [tableData, setTableData] = useState<BalanceCheckLeftTableDef[]>([]);//all data for table
    const tableDataFiltered = useMemo(() => {//we should useMemo instead of effect to avoid re-rendering
      return applyFilters(tableData);
    }, [props.showReportedBalances, props.warningsUpdate, tableData]);
    //ids of table with loaded detail
    const [loadedDetailedInfo, setLoadedDetailedInfo] = useState<string[]>([]);//using customNames as id since we don't have ids
    const [loadedRequestUri, setLoadedRequestUri] = useState("");//to avoid race conditions we check we're doing the correct request

    useEffect(() => {
      startLoadingDetailedInfo();
    },[loadedDetailedInfo])

    const startLoadingDetailedInfo = (): void => {
      //new request or no data or already all loaded, bail
      if (loadedRequestUri !== props.filters || tableData.length < 1 || tableData.length <= loadedDetailedInfo.length) {
        props.setShowSpinners(false);
        return;
      }
      const maxCounterValue = loadedDetailedInfo.length === 0 ? 20 : 150;//smaller first load
      const customNameFilterParts : string[] = [];
      var counter = 0;
      const itemsToLoad = tableData.map((i) => {
        const detailLoaded = loadedDetailedInfo.find((e) => e === i.customName);
        if (!detailLoaded && counter < maxCounterValue) {
          const mapping = getCustomNameMapping(i);
          var customNameForApi = mapping.customNameForApi;
          var customNameForMapping = mapping.customNameForMapping;

          const component = encodeURIComponent(customNameForApi);
          customNameFilterParts.push('filter[customNames][]=' + component);
          counter += 1;
          return customNameForMapping;
        }
          return null;
      });
      if (customNameFilterParts.length === 0) {
        props.setShowSpinners(false);
        return;
      }
      const customNameFilter : string = customNameFilterParts.join('&');
      if (!props.showSpinners) {
        props.setShowSpinners(true);
      }
      const itemsToLoadFiltered: string[] = itemsToLoad.filter(notEmpty);
      postLaminasApiData<TableDataRes<BalanceCheckLeftTableDef, any>>(
        '/tx-overview-v2/platform-overview',
        `${customNameFilter}&${props.filters}`,
        controllerDetailLoad.signal,
        {'Content-Type': 'application/x-www-form-urlencoded'}
      ).then((res: TableDataRes<BalanceCheckLeftTableDef, any>) => {
          const mapped = tableData.map((item) => {
            const resItem = res.data.find((e) => e.customName === item.customName);
            if (resItem) {
              const itemMapped = { ...item };
              itemMapped.totalValue = resItem.totalValue;
              itemMapped.balanceStatusSummary = resItem.balanceStatusSummary;
              itemMapped.coinTickerSummary = resItem.coinTickerSummary;
              itemMapped.containsLiveData = resItem.containsLiveData;
              return itemMapped;
            } else {
              return item;
            }
          });
          setTableData([...mapped]);
          const newLoadedDetailedInfo = loadedDetailedInfo.concat(itemsToLoadFiltered);
          const removeDuplicates = [...new Set(newLoadedDetailedInfo)];//should not have any
          if (removeDuplicates.length <= loadedDetailedInfo.length) {
            props.setShowSpinners(false);
            return;
          };
          setLoadedDetailedInfo([...removeDuplicates])
      });
    };

    const columns = useMemo<MRT_ColumnDef<BalanceCheckLeftTableDef>[]>(() => [
      {
        accessorKey: 'customName',
        minSize: 70,
        header: t('Exchange/Wallet', { ns: 'overview' }),
        Cell: (info) => {
          const items: string = info.cell.getValue() as string | '';
          const translatedItems: string = (items === '* no value *') ? t('* no value *', { ns: 'overview' }) : items;

          return !!items && items !== ''
            ? (
              <PlatformCard
              logo_uri={undefined}
              name={translatedItems}
              display_name={translatedItems}
              beta={false}
              modifiers={[PlatformCardModifier.OLD, PlatformCardModifier.OVERVIEW, PlatformCardModifier.SMALL_HEIGHT]}
            />
            )
            : <p />;
        },
      },
      {
        accessorKey: 'lastUpdated',
        minSize: 50,
        header: t('Last Updated', { ns: 'overview' }),
        accessorFn: (row) => getDateForUI(row.lastUpdated, userSettings?.dateFormat),
        Cell: ({ cell }) => (
          <div
            className="ct_tableBase-text-wrap-one-line"
            style={{ maxWidth: '110px' }}
          >
            {cell.getValue<string>() || ''}

          </div>
        ),
      },
      {
        accessorKey: 'transactionsCount',
        maxSize: 20,
        header: '',
        Header: ({ column }) => (
          <Tooltip
          cursor={TooltipCursorEnum.HELP}
            align={TooltipAlignEnum.LEFT}
            message={
              t('Amount of transactions', { ns: 'overview' })
            }
            modifiers={[]}
            key={column.id}
          >
            {t('Transactions', { ns: 'overview' })}
          </Tooltip>
        ),
        muiTableHeadCellProps: {
          align: 'right',
        },
        muiTableBodyCellProps: {
          align: 'right',
        },
        Cell: (info) => (
            <span key={info.cell.getValue() as string | ''}>{info.cell.getValue() as string | ''}</span>
        ),
      },
      {
        accessorKey: 'coinTickerSummary',
        header: '',
        Header: ({ column }) => (
          <Tooltip
          cursor={TooltipCursorEnum.HELP}
            align={TooltipAlignEnum.LEFT}
            message={
              t('Amount of coins/currencies', { ns: 'overview' })
            }
            modifiers={[]}
            key={column.id}
          >
            {t('Coins/Currencies', { ns: 'overview' })}
          </Tooltip>
        ),
        muiTableHeadCellProps: {
          align: 'right',
        },
        muiTableBodyCellProps: {
          align: 'right',
        },
        Cell: (info) => {
          const value: string[] = info.cell.getValue();
          if ((!value && value !== 0) || refreshCacheAction) {
            if (loadingStatus === OverviewLoadingStatus.LOADING_ERROR) {
              return <p>❌</p>;
            }
            return <CircularProgress size={20} />;
          }

           const items: string[] = info.cell.getValue() as string[] | [];

           if (items.length < 1) {
             return <span>0</span>;
           }

          const maxLength = 10;
          const limitItemLength = items.slice(0,maxLength);
          if (items.length > maxLength) {
            const difference = items.length - maxLength;
            limitItemLength.push(difference + ' ' + t('more...', { ns: 'overview' }));
          }

          return (
            <Tooltip
              align={TooltipAlignEnum.LEFT}
              cursor={TooltipCursorEnum.HELP}
              modifiers={[TooltipModifier.OVERVIEW]}
              tooltipChildren={(
                <IconList items={limitItemLength} modifiers={[IconListModifier.TABLE_SPACING]} />
              )}
            >
              <span>{items.length}</span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'totalValue',
        header: t('Total Value', { ns: 'overview' }),
        muiTableHeadCellProps: {
          align: 'right',
        },
        muiTableBodyCellProps: {
          align: 'right',
        },
        Cell: (info) => {
          const value: number = info.cell.getValue();
          if ((!value && value !== 0) || refreshCacheAction) {
            if (loadingStatus === OverviewLoadingStatus.LOADING_ERROR) {
              return <p>❌</p>;
            }
            return <CircularProgress size={20} />;
          }

          const formattedValue: JSX.Element | null = getFormattedNumber(
            value,
            userSettings?.decimalCharacter,
            userSettings?.thousandCharacter,
            userSettings.currency,
          );
          return (
            <Tooltip
              align={TooltipAlignEnum.LEFT}
              cursor={TooltipCursorEnum.HELP}
              modifiers={[TooltipModifier.OVERVIEW]}
              tooltipChildren={(
                <span key={value}>
              {formattedValue}
              {' '}
              {userSettings.currency}
            </span>
              )}
            >
              <span key={value}>
              {formattedValue}
              {' '}
              {userSettings.currency}
            </span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'balanceStatusSummary',
        header: '',
        muiTableHeadCellProps: {
          align: 'right',
        },
        muiTableBodyCellProps: {
          align: 'right',
        },
        Header: ({ column }) => {
          if (props.routeName !== 'balancecheck') {
            return null;
          }

          if ((cacheDateTime === null) && process.env.NODE_ENV !== 'development') {
            return null;
          }

          if (!hasLiveData(tableDataFiltered)) {
            return null;
          }

          const messageText = cacheDateTime === null ? (
            t('Refresh button is always shown in development environment', { ns: 'overview' })
          ) : (
            t('Live data cached as of', { ns: 'overview' })
            + ' '
            + getDateForUI(cacheDateTime, userSettings?.dateFormat)
          );
          return (
            <Tooltip
              align={TooltipAlignEnum.RIGHT}
              title={undefined}
              message={messageText}
              modifiers={[TooltipModifier.OVERVIEW]}
            >
              <div className="ct_tableFooter-cached-forceRefreshButton" onClick={(e) => {
                e.stopPropagation();
                setRefreshCacheAction(Date.now());
              }}>
                <RefreshIcon name="refreshIcon" />
              </div>
            </Tooltip>
          );
        },
        Cell: (info) => {
          const items: ImportTableBalanceStatus[] = info.cell.getValue() as ImportTableBalanceStatus[] || null;
          if (!items || refreshCacheAction) {
            if (loadingStatus === OverviewLoadingStatus.LOADING_ERROR) {
              return <p>❌</p>;
            }
            return <CircularProgress size={20} />;
          }

          const numberOfCoinsCurrencies = info.row.original.coinTickerSummary as string[] | [];
          if (numberOfCoinsCurrencies.length < 1) {
            return (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignContent: 'flex-end', }}>
                <BalanceStatusTooltip status={[]} isDetail={false} cointainsLiveData={info.cell.row.original.containsLiveData || false} />
              </div>
            );
          }

          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignContent: 'flex-end', }}>
              <BalanceStatusTooltip status={items} isDetail={false} cointainsLiveData={info.cell.row.original.containsLiveData || false} />
            </div>
          );
        },
      },
    ], [props.currentMismatch, props.userCurrencyMismatch, props.showSpinners, isFetching, loadingStatus]);

    return (
      <>
       <MaterialReactTable
        tableInstanceRef={tableRef}
        columns={columns}
        data={tableDataFiltered}
        initialState={{
          showColumnFilters: false,
          density: 'compact',
          columnVisibility: {
            'mrt-row-expand': false,
            detail: false,
          },
        }}
        uiTableProps={{
          sx: {
            tableLayout: 'fixed',
          },
        }}
        muiTableBodyRowProps={({ row }) => ({
          hover: true,
          onClick: (event) => {
            if (props.showSpinners && isFetching) return;
            event.stopPropagation();
            onDetailNavigate(row.original.customName);
          },
          sx: { cursor: (props.showSpinners && isFetching) ? 'not-allowed' : 'pointer' },
        })}
        muiTableBodyProps={({ row }) => ({
          sx: getStripedRowsForTable()
        })}
        muiTableHeadCellProps={({column}) => ({
          sx: {
            padding: '4px',
          },
        })}
        muiTableBodyCellProps={({cell}) => ({
          sx: {
            padding: '4px',
          },
        })}
        muiToolbarAlertBannerProps={
        isError
          ? {
            color: 'error',
            children: t('Error loading data'),
          }
          : undefined
      }
        enableColumnActions={false}
        enableColumnFilters={false}
        enablePagination={false}
        enableSorting={false}
        enableBottomToolbar={false}
        enableTopToolbar={false}
        defaultColumn={{
          size: 20,
          columnFilterModeOptions: [
            'fuzzy',
          ],
          enableColumnFilter: false,
          enableColumnFilterModes: false,
        }}
        state={{
          globalFilter: props.globalFilter,
          isLoading: showLoadingBars() && isLoading,
          showAlertBanner: isError,
          showProgressBars: false,
          showSkeletons: showLoadingBars() && isLoading,
          showGlobalFilter: true,
        }}
        muiFilterTextFieldProps={{
          sx: { m: '0.5rem 0', width: '100%' },
        }}
        muiTableContainerProps={{
          sx: {
            minWidth: '650px',//details autoexpands not to crop rows too much, but this table does not
          },
        }}
        />
      </>
    );
  }

  const queryClient = new QueryClient();

  function BalanceCheckLeftTableWithReactQueryProvider(props: BalanceCheckTableProps) {
    return (
      <QueryClientProvider client={queryClient}>
        <BalanceCheckLeftTable {...props} />
      </QueryClientProvider>
    );
  }

  export default BalanceCheckLeftTableWithReactQueryProvider;
